import { useQuery, useQueryClient } from '@tanstack/react-query'

import injuryReportsResources from '@/api/injuryReports'
import type { Notifications } from '@/api/notifications'
import queries from '@/api/queries'
import { useTranslation } from '@/components/i18n/hooks'
import { getRegexSafeString } from '@/util/misc'

import { INJURY_REPORT_STATUS_IDS } from './constants'
import { getInjuryReportStatusTextId } from './helpers'
import { useInjuryReportsContext } from './index.context'
import type { InjuryReportWithStatusText } from './types'

const getIsOrderOpen = (statusId: number) =>
  [
    INJURY_REPORT_STATUS_IDS.FOR_CONSIDERATION,
    INJURY_REPORT_STATUS_IDS.ASSET_UNDER_REPAIR,
  ].includes(statusId)

export const useInjuryReportsList = () => {
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const { searchFilter, statusFilter } = useInjuryReportsContext()

  const injuryReportListQuery = useQuery(queries.injuryReports(), injuryReportsResources.getList, {
    onSuccess(injuryReports) {
      const pendingInjuryReportAmount = injuryReports.filter(injuryReport =>
        getIsOrderOpen(injuryReport.statusId)
      ).length

      // Update injury reports notifications stats
      const currentNotifications: Notifications | undefined = queryClient.getQueryData(
        queries.notifications()
      )
      queryClient.setQueryData(queries.notifications(), {
        ...currentNotifications,
        pendingInjuryReportAmount,
      })
    },
  })

  const injuryReportListExtended = (injuryReportListQuery.data || []).map(injuryReport => ({
    ...injuryReport,
    statusText: translate(getInjuryReportStatusTextId(injuryReport.statusId)),
  }))

  const filteredList = filterReports({
    injuryReportList: injuryReportListExtended,
    searchFilter,
    statusFilter,
  })
  const numberOfInjuryReports = filteredList.length
  const statusCounter = getStatusCounter(injuryReportListExtended)

  return {
    list: filteredList,
    numberOfInjuryReports,
    statusCounter,
    ...injuryReportListQuery,
  }
}

const filterReports = ({
  injuryReportList,
  statusFilter,
  searchFilter,
}: {
  injuryReportList: InjuryReportWithStatusText[]
  statusFilter: number
  searchFilter: string
}) => {
  if (statusFilter === INJURY_REPORT_STATUS_IDS.ALL && !searchFilter) return injuryReportList

  const keyWords: string[] = getRegexSafeString(searchFilter).split(' ')

  return injuryReportList.filter(injuryReport => {
    const isStatusFilterSatisfied =
      injuryReport.statusId === statusFilter ||
      statusFilter === INJURY_REPORT_STATUS_IDS.ALL ||
      (statusFilter === INJURY_REPORT_STATUS_IDS.OPEN && getIsOrderOpen(injuryReport.statusId))

    const issearchFilterSatisfied = !searchFilter
      ? true
      : keyWords.every(keyWord => {
          const regExp = new RegExp(keyWord, 'i')

          return `${injuryReport.id} ${injuryReport.assetDescription} ${injuryReport.userName} ${injuryReport.statusText} ${injuryReport.customerName} ${injuryReport.phoneNumber}`.match(
            regExp
          )
        })

    return isStatusFilterSatisfied && issearchFilterSatisfied
  })
}

const getStatusCounter = (injuryReportList: InjuryReportWithStatusText[]) => {
  const statusCounter = injuryReportList.reduce(
    (acc: Partial<Record<number, number>>, { statusId }) => {
      acc[INJURY_REPORT_STATUS_IDS.ALL] = (acc[INJURY_REPORT_STATUS_IDS.ALL] || 0) + 1
      acc[INJURY_REPORT_STATUS_IDS.OPEN] =
        (acc[INJURY_REPORT_STATUS_IDS.OPEN] || 0) + (getIsOrderOpen(statusId) ? 1 : 0)
      acc[statusId] = (acc[statusId] || 0) + 1

      return acc
    },
    {}
  )

  return statusCounter
}
